/**
 * Global narrator lines for The Scientist (death quips, evolve lines, idle quips, intro,
 * endings). Stage-specific lines live with their stages. Keep them ≤ 120 chars, PG-13,
 * no show catchphrases. Burps are written as *burp*.
 */
import type { NarratorMood } from '@core/services';

export const DEATH_QUIPS: string[] = [
  "Aaand you're dead. Back to the start of the stage. Science is iterative.",
  'Wow. Natural selection just selected you. Out.',
  "Don't worry, I've got infinite copies of you. Try again. *burp*",
  "Devolved! This is why we can't have nice organisms.",
  'That was a bold strategy. It did not work. Rewinding.',
  "You know what the fossil record calls that? Nothing. It doesn't call it anything.",
  'Death is just evolution asking you to try again, with feeling.',
  'Extinct-ish. Luckily I keep backups. Mostly for me.',
  'Splat. Great. I had money on you lasting another minute.',
  'Ok so THAT is what "unfit" means. Good to see the theory holds up.',
  "Don't take it personally. Ninety-nine percent of species did that too.",
  'Your genes just wrote a strongly worded letter. Respawning.',
];

export const DEATH_QUIPS_MANY: string[] = [
  "That's a lot of dying. I'm starting to think it's you, not the environment.",
  'At this point I could name a graveyard after you. *burp*',
  "You've died more than my last three lab assistants combined. And they were flies.",
];

export const EVOLVE_LINES: string[] = [
  'Look at you, all grown up. Hold Evolve whenever you are ready.',
  "You're ready to evolve. Or keep eating. I'm not your mom.",
  'Evolution ready! Keep playing to push your mutation, or hold Evolve now.',
  "Full size. You can evolve, or you can keep bumbling around. Your call. *burp*",
];

export const TRANSITION_LINES: Record<string, string[]> = {
  default: [
    '{path}! Congratulations, you are now a {to}. Try not to waste it.',
    '{path}. Bold. Fine. Enjoy being a {to}, I guess.',
    'And just like that: {to}. Millions of years, skipped. You are welcome.',
    'Oh look, {path}. You picked {to}. I would have picked something with fewer teeth.',
  ],
};

export const IDLE_QUIPS: string[] = [
  "You're just... sitting there. Evolution rewards movement. Usually.",
  'Are you photosynthesizing or did you fall asleep?',
  'I have all day. Actually I have several hundred million years. Move.',
  'Fun fact: doing nothing is how fossils happen.',
  "The hint is in the HUD. The other hint is: don't die. *burp*",
];

/** Opening cinematic subtitles — `at`/`dur` in real seconds of the ~10 s cinematic. */
export const INTRO_LINES: Array<{ text: string; mood: NarratorMood; at: number; dur: number }> = [
  { text: 'Behold: the evolution ray! One zap and anything climbs the tree of life.', mood: 'excited', at: 0.2, dur: 3.2 },
  { text: "Hold this. Don't touch the dial— *burp*", mood: 'snark', at: 3.5, dur: 1.9 },
  { text: 'That was the REVERSE dial.', mood: 'alarm', at: 5.6, dur: 1.8 },
  { text: 'Welcome to the bottom of the tree. Chop chop.', mood: 'snark', at: 7.6, dur: 2.4 },
];

export const ENDING_LINES: Record<string, string> = {
  algae: 'Pond scum. You had the whole tree of life and you chose pond scum. Honestly, iconic.',
  jellyfish: "No brain, no spine, immortal. You did what I've been trying to do with three PhDs.",
  shark: 'Four hundred million years and nobody improved on you. Not even me. Don\'t tell anyone I said that.',
  dinosaur: 'You ruled the planet! For a while! Then a rock happened. Rocks are undefeated.',
  bird: 'You survived the rock by being airborne and adorable. Cheating, but I respect it.',
  whale: 'You went BACK to the ocean. With lungs. That is the most human thing a non-human has ever done.',
  wolf: 'Pack leader. Congratulations, in a few thousand years your kids will be named "Biscuit".',
  chimp: 'Ninety-eight point eight percent human and zero percent taxes. Who is the smart one here.',
  human: "And there it is. Fire, tools, a ray gun that turns people into cells. See you at the bottom. *burp*",
};

export const LOADING_TIPS: string[] = [
  'Your behaviour decides your branch. What you eat and where you go feeds the mutation meter.',
  'Dying only devolves you to the start of the stage. Your growth mostly survives.',
  'Every stage has a dodge and a strike. Learn the enemy telegraphs.',
  'Hold Evolve when the meter is full, or keep playing to shift your mutation first.',
  'Traits earned in one form carry forward as small stat bonuses for the rest of the run.',
  'Ties on the mutation meter go to the first branch. Commit to a path!',
  'On phones, drag the left half to move and the right half to look around.',
  'The Evolution Tree lets you restart from any form you have discovered.',
  'There are nine endings. Only one of them is the "right" one. Allegedly.',
  'Gamepads work everywhere: sticks to move and look, A/B/X/Y for actions, Start to pause.',
  'Sunlight, currents, burrows and treetops all matter. Explore the edges of each world.',
  'Screen shake, subtitles, reduced motion and colour-blind branch patterns live in Settings.',
];

export const HUD_HINT_IDLE = 40; // seconds without growth before an idle quip

/** Replace {path}/{to}/{from} tokens. */
export function fill(line: string, vars: Record<string, string>): string {
  return line.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '');
}
